import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Loader2, CheckCircle, XCircle, Clock, Package, 
  AlertCircle, Image as ImageIcon, ArrowRight
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductImportProgress = () => {
  const { importId } = useParams();
  const navigate = useNavigate();
  const [importData, setImportData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchImportStatus();
    
    // Poll every 2 seconds if import is running
    const interval = setInterval(() => {
      if (importData?.status === 'running' || importData?.status === 'pending') {
        fetchImportStatus();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [importId, importData?.status]);

  const fetchImportStatus = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/products/import-status/${importId}`);
      setImportData(response.data);
      setLoading(false);

      // If completed, show toast
      if (response.data.status === 'completed' && importData?.status !== 'completed') {
        toast.success('Import abgeschlossen');
      } else if (response.data.status === 'failed' && importData?.status !== 'failed') {
        toast.error('Import fehlgeschlagen');
      }
    } catch (error) {
      console.error('Error fetching import status:', error);
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-8 w-8 text-yellow-600" />;
      case 'running':
        return <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-8 w-8 text-green-600" />;
      case 'failed':
        return <XCircle className="h-8 w-8 text-red-600" />;
      default:
        return <Clock className="h-8 w-8 text-gray-600" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Wartend';
      case 'running':
        return 'Läuft';
      case 'completed':
        return 'Abgeschlossen';
      case 'failed':
        return 'Fehlgeschlagen';
      default:
        return 'Unbekannt';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (!importData) {
    return (
      <DashboardLayout>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Import nicht gefunden</p>
            <Button onClick={() => navigate('/superadmin/products')} className="mt-4">
              Zurück zur Produktverwaltung
            </Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  const isRunning = importData.status === 'running' || importData.status === 'pending';
  const isCompleted = importData.status === 'completed';
  const isFailed = importData.status === 'failed';

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Status Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              {getStatusIcon(importData.status)}
              <div>
                <div>{getStatusText(importData.status)}</div>
                <div className="text-sm font-normal text-muted-foreground">{importData.filename}</div>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Progress Bar */}
            {isRunning && (
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Fortschritt</span>
                  <span className="font-medium">{importData.progress || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${importData.progress || 0}%` }}
                  ></div>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {importData.rows_success || 0} von {importData.rows_total} Zeilen verarbeitet
                </p>
              </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{importData.rows_total}</div>
                <div className="text-xs text-muted-foreground mt-1">Gesamt</div>
              </div>
              <div className="text-center p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{importData.rows_success || 0}</div>
                <div className="text-xs text-muted-foreground mt-1">Erfolgreich</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{importData.rows_skipped || 0}</div>
                <div className="text-xs text-muted-foreground mt-1">Übersprungen</div>
              </div>
              <div className="text-center p-4 bg-red-50 dark:bg-red-950 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{importData.rows_failed || 0}</div>
                <div className="text-xs text-muted-foreground mt-1">Fehler</div>
              </div>
            </div>

            {/* Report Data (if completed) */}
            {isCompleted && importData.report_data && (
              <div className="space-y-4">
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">Import-Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2 text-sm">
                      <ImageIcon className="h-4 w-4 text-green-600" />
                      <span className="text-muted-foreground">Mit Bild (von OpenFoodFacts):</span>
                      <span className="font-medium">{importData.report_data.with_image || 0}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Package className="h-4 w-4 text-yellow-600" />
                      <span className="text-muted-foreground">Ohne Bild:</span>
                      <span className="font-medium">{importData.report_data.without_image || 0}</span>
                    </div>
                  </div>
                </div>

                {/* Duplicates */}
                {importData.report_data.duplicates && importData.report_data.duplicates.length > 0 && (
                  <div className="border-t pt-4">
                    <h3 className="font-medium mb-3 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-yellow-600" />
                      Duplikate ({importData.report_data.duplicates.length})
                    </h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {importData.report_data.duplicates.map((dup, idx) => (
                        <div key={idx} className="bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded p-3 text-sm">
                          <div className="font-mono text-xs mb-1">GTIN: {dup.gtin}</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <span className="text-muted-foreground">Bestand:</span>
                              <div className="font-medium">{dup.existing_name}</div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">CSV:</span>
                              <div className="font-medium">{dup.csv_name}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Error (if failed) */}
            {isFailed && importData.report_data?.error && (
              <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded p-4">
                <h3 className="font-medium mb-2 flex items-center gap-2 text-red-600">
                  <XCircle className="h-4 w-4" />
                  Fehler
                </h3>
                <p className="text-sm">{importData.report_data.error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
              {isCompleted && (
                <Button onClick={() => navigate('/superadmin/products')} className="flex-1">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Zur Produktverwaltung
                </Button>
              )}
              {isFailed && (
                <Button onClick={() => navigate('/superadmin/products/import')} variant="outline" className="flex-1">
                  Neue CSV hochladen
                </Button>
              )}
              {isRunning && (
                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded p-4 text-sm text-center">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2 text-blue-600" />
                  <p>Sie können diese Seite verlassen. Der Import läuft im Hintergrund weiter.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card>
          <CardHeader>
            <CardTitle>Zeitverlauf</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div className="flex-1">
                  <div className="font-medium text-sm">Import gestartet</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(importData.started_at).toLocaleString('de-DE')}
                  </div>
                </div>
              </div>
              {importData.completed_at && (
                <div className="flex items-center gap-3">
                  {isCompleted ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <div className="flex-1">
                    <div className="font-medium text-sm">Import abgeschlossen</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(importData.completed_at).toLocaleString('de-DE')}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
